/**
 * BarRow — one labelled horizontal bar.
 *
 * Deliberately single-hue rather than a multi-colour palette. The dataviz
 * validator rejected both 8-hue (role) and 5-hue (escrow status) categorical
 * sets for this surface: teal/cyan fell under the chroma floor and read grey,
 * and red↔green sat at ΔE 5.0 for deuteranopia. Every bar here is directly
 * labelled, so identity never depends on colour — the only second hue is the
 * reserved status red, used for genuinely critical rows and always paired with
 * a label and icon.
 *
 * Mark spec: 8px track, 4px rounded data-end, value label outside the bar,
 * recessive track, hover tooltip carrying the exact figures.
 */

import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { brand, text, border, easing } from '../../theme/colors';

const BarRow = ({
  label,
  value,
  max,
  display,
  caption,
  tone = brand.primary,
  icon,
  tooltip,
  onClick,
}) => {
  // Guard against a zero max: an all-zero dataset must not divide by zero.
  const pct = max > 0 ? Math.max(value > 0 ? 2 : 0, (value / max) * 100) : 0;

  const row = (
    <Box
      component={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        border: 'none',
        bgcolor: 'transparent',
        p: 0,
        mb: 1.5,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover .bar-label': onClick ? { color: text.primary } : {},
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5 }}>
        {icon}
        <Typography
          className="bar-label"
          sx={{
            fontSize: 13,
            color: text.secondary,
            textTransform: 'capitalize',
            transition: `color 140ms ${easing.smooth}`,
            flex: 1,
            minWidth: 0,
          }}
          noWrap
        >
          {label}
        </Typography>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: text.primary, whiteSpace: 'nowrap' }}>
          {display ?? value}
        </Typography>
        {caption && (
          <Typography variant="caption" sx={{ color: text.tertiary, whiteSpace: 'nowrap' }}>
            {caption}
          </Typography>
        )}
      </Box>

      <Box sx={{ height: 8, borderRadius: 999, bgcolor: border.light, overflow: 'hidden' }}>
        <Box
          sx={{
            width: `${pct}%`,
            height: '100%',
            bgcolor: tone,
            borderRadius: 999,
            transition: `width 320ms ${easing.decel}`,
          }}
        />
      </Box>
    </Box>
  );

  return tooltip ? <Tooltip title={tooltip} arrow placement="top">{row}</Tooltip> : row;
};

export default BarRow;
